/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Flag } from 'ui/atoms/Flag/Flag';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './LegalMonitoring.module.scss';

type Row = {
  id: number;
  cat: string;
  hi: boolean;
  h: string;
  e: string;
  code: string;
  src: string;
  secs: number;
  isNew?: boolean;
};

const CHIPS = [
  { f: 'all', label: 'All events' },
  { f: 'sanctions', label: 'Sanctions' },
  { f: 'control', label: 'Control' },
  { f: 'ownership', label: 'Ownership' },
  { f: 'charges', label: 'Charges' },
  { f: 'officers', label: 'Officers' },
  { f: 'status', label: 'Status' },
];

const INITIAL_ROWS: Row[] = [
  {
    id: 0,
    cat: 'control',
    hi: true,
    h: 'Control moved above the Dutch holder',
    e: 'Halden Group Holdings BV',
    code: 'lu',
    src: 'RCS Luxembourg',
    secs: 1,
  },
  {
    id: 1,
    cat: 'address',
    hi: false,
    h: 'Registered office changed mid-matter',
    e: 'Halden Marine Services Ltd',
    code: 'gb',
    src: 'Companies House',
    secs: 26,
  },
  {
    id: 2,
    cat: 'status',
    hi: true,
    h: 'Company dissolved',
    e: 'Kestrel Offshore GmbH',
    code: 'de',
    src: 'Handelsregister',
    secs: 60,
  },
  {
    id: 3,
    cat: 'charges',
    hi: false,
    h: 'Fixed and floating registered to a new lender',
    e: 'Halden Marine Services Ltd',
    code: 'gb',
    src: 'Companies House',
    secs: 120,
  },
  {
    id: 4,
    cat: 'officers',
    hi: false,
    h: 'Signatory on the facility resigned',
    e: 'Brant Chartering Pte Ltd',
    code: 'sg',
    src: 'ACRA',
    secs: 180,
  },
  {
    id: 5,
    cat: 'status',
    hi: true,
    h: 'Sister entity entered administration',
    e: 'Halden Cold Chain Ltd',
    code: 'ie',
    src: 'CRO Ireland',
    secs: 240,
  },
  {
    id: 6,
    cat: 'sanctions',
    hi: true,
    h: 'Parent added to a consolidated list',
    e: 'Brant Maritime Holdings SA',
    code: 'lu',
    src: 'RCS Luxembourg',
    secs: 360,
  },
];

const QUEUE = [
  {
    cat: 'sanctions',
    hi: true,
    h: 'Designation added to an entity in the chain',
    e: 'Brant Marine Insurance Ltd',
    code: 'gb',
    src: 'Companies House',
  },
  {
    cat: 'ownership',
    hi: false,
    h: 'Existing client incorporated three new entities',
    e: 'Halden Group Holdings BV',
    code: 'nl',
    src: 'KvK Handelsregister',
  },
  {
    cat: 'control',
    hi: true,
    h: 'Right to appoint the board transferred',
    e: 'Brant Maritime Holdings SA',
    code: 'lu',
    src: 'RCS Luxembourg',
  },
  {
    cat: 'officers',
    hi: false,
    h: 'Two directors resigned within seven days',
    e: 'Adlerhaven Bunkering AS',
    code: 'no',
    src: 'Brønnøysundregistrene',
  },
  {
    cat: 'status',
    hi: true,
    h: 'Sub-tier entity marked dissolved',
    e: 'Halden Bunkering Services Ltd',
    code: 'gb',
    src: 'Companies House',
  },
];

const MAX_ROWS = 9;

const CATEGORY_COLOR: Record<string, string> = {
  sanctions: '#E93333',
  control: '#3642F5',
  ownership: '#8A33D6',
  charges: '#B26F0E',
  officers: '#1FA37B',
  status: '#3EA8CF',
  address: '#8B93A7',
};

const PERIODIC_DOTS = [2, 50, 98];
const PERPETUAL_DOTS = Array.from({ length: 31 }, (_, i) => 2 + i * 3.2);

const fmt = (sec: number) => {
  if (sec < 60) {
    return `${sec}s`;
  }
  if (sec < 3600) {
    return `${Math.floor(sec / 60)}m`;
  }
  return `${Math.floor(sec / 3600)}h`;
};

const formatClock = () => {
  const d = new Date();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
};

export const LegalMonitoring = () => {
  const { t } = useInt();
  const [rows, setRows] = useState<Row[]>(INITIAL_ROWS);
  const [filter, setFilter] = useState('all');
  const [paused, setPaused] = useState(false);
  const [clock, setClock] = useState('');

  const pausedRef = useRef(false);
  const qiRef = useRef(0);
  const idRef = useRef(INITIAL_ROWS.length);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  const addEvent = () => {
    const ev = QUEUE[qiRef.current % QUEUE.length];
    qiRef.current += 1;
    const id = idRef.current;
    idRef.current += 1;

    setRows((prev) => {
      const next = [{ id, ...ev, secs: 0, isNew: true }, ...prev];
      return next.length > MAX_ROWS ? next.slice(0, MAX_ROWS) : next;
    });

    setTimeout(() => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isNew: false } : r)));
    }, 550);
  };

  useEffect(() => {
    setClock(formatClock());

    const clockId = setInterval(() => {
      if (pausedRef.current) {
        return;
      }
      setClock(formatClock());
      setRows((prev) => prev.map((r) => ({ ...r, secs: r.secs + 1 })));
    }, 1000);

    const eventId = setInterval(() => {
      if (pausedRef.current || document.hidden) {
        return;
      }
      addEvent();
    }, 3000);

    return () => {
      clearInterval(clockId);
      clearInterval(eventId);
    };
  }, []);

  const visibleRows = rows.filter((r) => filter === 'all' || r.cat === filter);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          title={t('Perpetual monitoring. The file moves when the register moves.')}
          description={t(
            'Periodic review re-checks a client when someone remembers to. Perpetual monitoring watches every entity in the matter continuously, so a filing reaches the fee earner the day it lands.',
          )}
        />
      </div>

      <div className={styles.mc}>
        <div className={styles.mcRow}>
          <span className={styles.mcIcon}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="10" cy="10" r="7.5" />
              <path d="M10 6v4l2.6 1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className={styles.mcK}>{t('Periodic')}</span>
          <div className={styles.mcTrack}>
            {PERIODIC_DOTS.map((pos) => (
              <i style={{ left: `${pos}%` }} key={pos} />
            ))}
          </div>
          <span className={styles.mcD}>
            {t('Three snapshots across the retainer. Everything between them is assumed.')}
          </span>
        </div>
        <div className={cn(styles.mcRow, styles.mcRowOn)}>
          <span className={styles.mcIcon}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M2 10h3.4l1.8-5 3 9.5 2-6.5 1.4 2h4.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className={styles.mcK}>{t('Perpetual')}</span>
          <div className={styles.mcTrack}>
            {PERPETUAL_DOTS.map((pos) => (
              <em style={{ left: `${pos}%` }} key={pos} />
            ))}
          </div>
          <span className={styles.mcD}>
            <b>{t('Every filing, the day it reaches the register.')}</b> {t('Nothing between is assumed.')}
          </span>
        </div>
      </div>

      <div className={cn(styles.lw, paused && styles.lwPaused)}>
        <div className={styles.lwTop}>
          <span className={styles.lwDot} />
          <span className={styles.lwLive}>Live</span>
          <span className={styles.lwName}>Perpetual monitoring</span>
          <span className={styles.lwMeta}>313 entities · 47 matters</span>
          <span className={styles.lwClock}>{clock}</span>
        </div>

        <div className={styles.lwBar}>
          {CHIPS.map((chip) => (
            <button
              type="button"
              className={cn(styles.lwChip, filter === chip.f && styles.lwChipOn)}
              onClick={() => setFilter(chip.f)}
              key={chip.f}
            >
              {chip.label}
            </button>
          ))}
          <button type="button" className={styles.lwPause} onClick={() => setPaused((p) => !p)}>
            <i />
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>

        <div className={styles.lwList}>
          {visibleRows.map((row) => (
            <div
              className={cn(styles.lwRow, row.hi && styles.lwRowHi, row.isNew && styles.lwRowNew)}
              style={{ '--cat-color': CATEGORY_COLOR[row.cat] ?? CATEGORY_COLOR.address } as CSSProperties}
              key={row.id}
            >
              <span className={styles.lwCat}>
                <b />
                {row.cat.toUpperCase()}
              </span>
              <span>
                <span className={styles.lwH}>{row.h}</span>
                <span className={styles.lwE}>{row.e}</span>
              </span>
              <span className={styles.lwSrc}>
                <Flag code={row.code} className={styles.lwFlag} />
                {row.src}
              </span>
              <span className={styles.lwT}>{fmt(row.secs)}</span>
            </div>
          ))}
        </div>

        <div className={styles.lwFoot}>
          <span>Sample stream</span>
          <span>Every alert carries the registry and the filing date behind it</span>
        </div>
      </div>

      <p className={styles.note}>
        {t(
          'Consequences shown are general orientation, not legal advice, and vary by jurisdiction and by the stage a matter has reached.',
        )}
      </p>

      <div className={styles.cta}>
        <Button type="blue" size="xl" href="/continuous-monitoring">
          {t('See how monitoring works')}
        </Button>
      </div>
    </Section>
  );
};
